import { useEffect } from "react";
import { useSelector } from "react-redux";
import { Navigate, useLocation, useNavigate, useParams, useRoutes } from "react-router-dom";
import { getClientPortalUser, getSupplierPortalUser } from "../actions/Auth/actions";
import { Loading } from "./Helper/Loading";
import NoMatch404 from "./NoMatch";
import Check from "./scenes/AddItem";
import AdminPage from "./scenes/Admin/AdminPage";
import Login from "./scenes/Auth/Login";
import Logout from "./scenes/Auth/Logout";
import { RequireAuth } from "./scenes/Auth/RequireAuth";
import { ClientPortal } from "./scenes/ClientPortal";
import DeviceManagement from "./scenes/DeviceManagement";
import DeviceConfiguration from "./scenes/DeviceManagement/DeviceManagement";
import HomeRedirect from "./scenes/HomeRedirect";
import LabellingStart from "./scenes/Labelling";
import LabellingManualSession from "./scenes/Labelling/ManualLabelSession";
import LabellingNewSession from "./scenes/Labelling/NewLabelling";
import LayerIndex from "./scenes/Layers/Layer/LayerIndex";
import PDFCheckReport from "./scenes/Layers/Layer/Report/PDFCheckReport";
import PDFLayerReport from "./scenes/Layers/Layer/Report/index";
import LayerStart from "./scenes/Layers/LayerStart";
import Mailbox from "./scenes/Mailbox/Mailbox";
import PackingListList from "./scenes/PackingList/PackingListList";
import PowerBI from "./scenes/PowerBi/PowerBI";
import RipeningRoom from "./scenes/Ripening/RipeningRoom";
import RipeningCellsTableWrapped from "./scenes/Ripening/RipeningRoomTable";
import Settings from "./scenes/Settings";
import SignalsPage from "./scenes/Signals/SignalsPage";
import SourcingList from "./scenes/SourcingList";
import { SupplierPortal } from "./scenes/SupplierPortal";
import Tenants from "./scenes/Tenants";
import TenantEditConfig from "./scenes/Tenants/TenantEditConfig";
import TenantEditDefectsForm from "./scenes/Tenants/TenantEditDefectsForm";
import TenantEditFacilityLocationsForm from "./scenes/Tenants/TenantEditFacilityLocationsForm";
import TenantEditForm from "./scenes/Tenants/TenantEditForm";
import TenantEditOptionsForm from "./scenes/Tenants/TenantEditOptionsForm";
import { isClientPortal, isSupplierPortal } from "./utils";

/**
 * Redirects legacy paths (e.g. /layer/...) to the tenant-prefixed equivalents
 * (e.g. /{tenant_id}/layer/...). Rendered inside RequireAuth so the user is
 * already authenticated and user.tenant.tenant_id is available.
 *
 * NOTE: This is a backwards-compatibility shim. Monitor console warnings to track
 * when legacy paths are being used. Once all links are migrated to tenant-prefixed
 * URLs, this can be removed.
 */
function TenantPrefixRedirect() {
    const tenantId = useSelector((state: any) => state.auth.user?.tenant?.tenant_id);
    const location = useLocation();
    const navigate = useNavigate();

    useEffect(() => {
        if (tenantId) {
            // eslint-disable-next-line no-console
            console.warn(
                `[Backwards Compat] Legacy path detected: ${location.pathname}${location.search} → /${tenantId}${location.pathname}${location.search}. `
                + `Update links to use tenant-prefixed URLs.`
            );
            navigate(`/${tenantId}${location.pathname}${location.search}`, { replace: true });
        }
    }, [tenantId, location.pathname, location.search]);

    return <Loading />;
}

function DeviceManagmentRedirect() {
    const params = useParams();
    const rest = params["*"] || "";
    const target = rest ? `/device-management/${rest}` : "/device-management";
    return <Navigate to={target} replace />;
}

const sharedRoutes = [
    {
        path: "/login",
        element: <Login />,
    },
    {
        path: "/logout",
        element: <Logout />,
    },
];

const appRoutes = [
    ...sharedRoutes,
    {
        path: "/settings",
        element: <RequireAuth element={<Settings />} />,
    },
    {
        path: "/",
        element: <RequireAuth element={<TenantPrefixRedirect />} />,
    },
    // ---------------------------------------------------------------------------
    // Tenant-scoped routes: /{tenant_id}/{section}/...
    // RequireAuth with prefixTenant verifies the user belongs to that tenant and
    // silently switches if needed (admins can access any tenant).
    // ---------------------------------------------------------------------------
    {
        path: "/:tenant_id",
        children: [
            {
                index: true,
                element: <RequireAuth prefixTenant element={<HomeRedirect />} />,
            },
            {
                path: "layer",
                children: [
                    {
                        index: true,
                        element: <RequireAuth prefixTenant element={<HomeRedirect />} />,
                    },
                    {
                        path: "tab/:tab_category/:active_tab",
                        element: <RequireAuth prefixTenant element={<LayerStart />} />,
                    },
                    {
                        path: "tab/:tab_category/:active_tab/edit-filter/:edit_field_filter",
                        element: <RequireAuth prefixTenant element={<LayerStart />} />,
                    },
                    {
                        path: ":layer_id",
                        element: <RequireAuth prefixTenant element={<LayerIndex />} />,
                    },
                    // * Layer reports, template = intake, store or advance_ripening
                    {
                        path: ":layer_id/report/:config_value",
                        element: <RequireAuth prefixTenant disableRefresh={true} element={<PDFLayerReport />} />,
                    },
                    {
                        path: ":layer_id/add-check/:check_id/:action/modal/:modal_type",
                        element: <RequireAuth prefixTenant element={<Check />} />,
                    },
                    {
                        path: ":layer_id/add-check/:check_id/:action/:avocado_id",
                        element: <RequireAuth prefixTenant element={<Check />} />,
                    },
                    // * Generic check summary reports. template=last_check
                    {
                        path: ":layer_id/add-check/:check_id/report",
                        element: <RequireAuth prefixTenant disableRefresh={true} element={<PDFCheckReport />} />,
                    },
                    {
                        path: ":layer_id/add-check/:check_id/:action",
                        element: <RequireAuth prefixTenant element={<Check />} />,
                    },
                    {
                        path: ":layer_id/add-check/:check_id",
                        element: <RequireAuth prefixTenant element={<Check />} />,
                    },
                    {
                        path: ":layer_id/action/:action",
                        element: <RequireAuth prefixTenant element={<LayerIndex />} />,
                    },
                ],
            },
            {
                path: "ripening-room",
                children: [
                    {
                        index: true,
                        element: <RequireAuth prefixTenant element={<RipeningCellsTableWrapped />} />,
                    },
                    {
                        path: ":location_id/from/:from_date/to/:to_date",
                        element: <RequireAuth prefixTenant element={<RipeningRoom />} />,
                    },
                    {
                        path: ":location_id/from/:from_date/to/:to_date/edit/:edit_date",
                        element: <RequireAuth prefixTenant element={<RipeningRoom />} />,
                    },
                    {
                        path: ":location_id",
                        element: <RequireAuth prefixTenant element={<RipeningRoom />} />,
                    },
                ],
            },
            {
                path: "packing-list",
                children: [{
                    index: true,
                    element: <RequireAuth prefixTenant element={<PackingListList />} />,
                }],
            },
            {
                path: "mailbox",
                children: [{
                    index: true,
                    element: <RequireAuth prefixTenant element={<Mailbox />} />,
                }],
            },
            {
                path: "signals",
                children: [
                    {
                        index: true,
                        element: <RequireAuth prefixTenant element={<SignalsPage />} />,
                    },
                    {
                        path: "view/:view",
                        element: <RequireAuth prefixTenant element={<SignalsPage />} />,
                    },
                ],
            },
            {
                path: "admin/*",
                element: <RequireAuth prefixTenant element={<AdminPage />} />,
            },
            {
                path: "sourcing-list",
                element: <RequireAuth prefixTenant element={<SourcingList />} />,
            },
        ],
    },
    // ---------------------------------------------------------------------------
    // Legacy redirects: bare paths without tenant prefix → /{tenant_id}/...
    // ---------------------------------------------------------------------------
    {
        path: "/layer/*",
        element: <RequireAuth element={<TenantPrefixRedirect />} />,
    },
    {
        path: "/ripening-room/*",
        element: <RequireAuth element={<TenantPrefixRedirect />} />,
    },
    {
        path: "/packing-list/*",
        element: <RequireAuth element={<TenantPrefixRedirect />} />,
    },
    {
        path: "/mailbox/*",
        element: <RequireAuth element={<TenantPrefixRedirect />} />,
    },
    {
        path: "/signals/*",
        element: <RequireAuth element={<TenantPrefixRedirect />} />,
    },
    {
        path: "/admin/*",
        element: <RequireAuth element={<TenantPrefixRedirect />} />,
    },
    {
        path: "/sourcing-list/*",
        element: <RequireAuth element={<TenantPrefixRedirect />} />,
    },
    {
        path: "/tenants",
        children: [
            {
                index: true,
                element: <RequireAuth element={<Tenants />} />,
            },
            {
                path: ":tenant_id",
                element: <RequireAuth element={<TenantEditForm />} />,
            },
            {
                path: ":tenant_id/tab/:config_tab",
                element: <RequireAuth element={<TenantEditForm />} />,
            },
            {
                path: ":tenant_id/form-field-options/defects", // * alias for defects
                element: <RequireAuth element={<TenantEditDefectsForm />} />,
            },
            {
                path: ":tenant_id/form-field-options/:field",
                element: <RequireAuth element={<TenantEditOptionsForm />} />,
            },
            {
                path: ":tenant_id/defects/:fruit_type",
                element: <RequireAuth element={<TenantEditDefectsForm />} />,
            },
            {
                path: ":tenant_id/facility-locations",
                element: <RequireAuth element={<TenantEditFacilityLocationsForm />} />,
            },
            {
                path: ":tenant_id/config/:config_id",
                element: <RequireAuth element={<TenantEditConfig />} />,
            },
        ],
    },
    {
        path: "/labelling",
        children: [
            {
                index: true,
                element: <RequireAuth element={<LabellingStart />} />,

            },
            {
                path: "new",
                element: <RequireAuth element={<LabellingNewSession />} />,
            },
            {
                path: ":session_id",
                element: <RequireAuth element={<LabellingManualSession />} />,
            },

        ],
    },
    // redirect for typo in old links - can be removed after some time
    {
        path: "/device-managment/*",
        element: <DeviceManagmentRedirect />,
    },
    {
        path: "/device-management",
        children: [
            {
                index: true,
                element: <RequireAuth element={<DeviceManagement />} />,
            },
            {
                path: ":device_uid/view/:view",
                element: <RequireAuth element={<DeviceConfiguration />} />,
            },
            {
                path: ":device_uid",
                element: <RequireAuth element={<DeviceConfiguration />} />,
            },
        ],
    },
    {
        path: "/power-bi/*",
        element: <RequireAuth bgWhite element={<PowerBI />} />,
    },
    {
        path: "*",
        element: <NoMatch404 />
    },
];

const supplierPortalRoutes = [
    ...sharedRoutes,
    {
        path: "/",
        element: <RequireAuth element={<SupplierPortal />} getUser={getSupplierPortalUser} />,
    },
    {
        path: "*",
        element: <NoMatch404 />,
    },
];

const clientPortalRoutes = [
    ...sharedRoutes,
    {
        path: "/",
        element: <RequireAuth element={<ClientPortal />} getUser={getClientPortalUser} />,
    },
    {
        path: "*",
        element: <NoMatch404 />,
    },
];

export default function MainContent() {
    // Determine which route configuration to use
    // eslint-disable-next-line no-nested-ternary
    const items = isSupplierPortal() ? supplierPortalRoutes : isClientPortal() ? clientPortalRoutes : appRoutes;
    const router = useRoutes(items);
    // TODO: wrap this in ConfigProvider... else it will load config all the time??
    return router;
}
